/**
 * ContentDataForm
 * ----------------
 * WHAT:
 *   Renders a single "page" of a content document (Pattern / Guide / Strategy).
 *   A page is a ContentNode whose children are ContentSections (also ContentNodes).
 *   Each section is rendered with a type-specific UI component (rich text editor,
 *   Epics table, or Troubleshooting table) and can be sorted by drag-and-drop
 *   (dnd-kit SortableList) when the caller enables `sectionsSortable`.
 *
 * WHY:
 *   The same form is reused in both edit mode and readonly (published) mode.
 *   It wires together:
 *     - the section template (which decides what UI widget to render)
 *     - the user-editable content
 *     - comments / tech-writer metadata / page-title editing modal
 *   Centralizing this logic here keeps the workflow viewer and the editor in sync.
 *
 * DATA FLOW (top-down):
 *   props.contentPage.contentSections -> ContentSections (SortableList)
 *     -> ContentSection (per section)
 *        -> ContentDataSection | PatternContentEpics | PatternContentTroubleshooting
 *
 * CALLBACKS (bottom-up):
 *   onContentChanged / onSectionsUpdated / onSectionDeleted / onPageTitleUpdated
 *   bubble up to the parent (EditContentData etc.) which persists to the service.
 */

// CloudScape primitives used to lay out the page-edit modal and containers.
import {
  Box,
  Button,
  Container,
  FormField,
  Grid,
  Header,
  Modal,
} from '@amzn/awsui-components-core';
import { SpaceBetween } from '@amzn/awsui-components-core';
// Service model types describing the shape of a workflow / page / user.
import {
  ContentAccessUser,
  ContentHistory,
  ContentNode,
  ContentWorkflow,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { reorderById } from '../../common/Arrays';
// ContentUtil helpers:
//   - findNodeById: recursive tree search used when syncing tech-writer metadata.
//   - getNodeHref: auto-generates a URL slug for a section (used for deep linking).
//   - guideOrStrategy: contentType is Guide or Strategy (these allow custom sections).
import { findNodeById, getNodeHref, guideOrStrategy } from '../../api/ContentUtil';
import constants from '../../common/constants';
// EditorInput = rich-text (Tiptap) editor used for the page body.
import EditorInput from '../../editor/EditorInput';
// Enum of keys that identify which field of a ContentNode is being edited
// (abstract / href / contentData / title).
import { ContentSectionKey } from '../../model/ContentSchemaSection';
// Enum that maps a template section to its UI widget (ContentDataSection, Epics, Troubleshooting).
import { ContentUIComponent } from '../../model/Enum';
// Sectional comments shown underneath each section.
import CommentThreadsView from '../comment/CommentThreadsView';
// The three supported section renderers. See hideEmpty* wrappers below.
import ContentDataSection, {
  ContentDataSectionProps,
} from '../workflow-viewer/content/ContentDataSection';
import PatternContentEpics, {
  PatternContentEpicsProps,
} from '../workflow-viewer/content/PatternContentEpics';
import PatternContentTroubleshooting, {
  PatternContentTroubleshootingProps,
} from '../workflow-viewer/content/PatternContentTroubleshooting';
// Abstract + href editor shown for tech-writer role at the page level.
import TechwriterMetadataEditor from '../workflow-viewer/content/TechwriterMetadataEditor';
// React context that exposes the current Content, inline comments, and
// helpers like addNewSection / updateContent to the whole viewer.
import {
  ContentViewerContextData,
  withContentViewerContext,
} from '../workflow-viewer/ContentViewerContext';
// Button that calls ContentViewerContext.addNewSection(). Rendered only for Guide/Strategy.
import { AddNewSectionButton } from './AddNewSectionButton';
// Header with title + edit/delete/sort controls.
import ContentSectionHeader from './ContentSectionHeader';
// Regexes used to decide whether readonly content is "effectively empty" and should be hidden.
import { emptyOrSpaceOnlyRegex, htmlTagsRegex, imgTagRegex } from '../utils/ContentDataUtils';
// Plain Tiptap editor used inside the "Edit Page title" modal.
import EditorWrapper from '../../editor/EditorWrapper';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { TiptapMenuType } from '../../editor/utils';
// AppContext exposes globally-loaded entities (AWS services / products) used by
// the editor's "@mention" style entity lookups inside the Troubleshooting editor.
import AppContextProps from '../../contexts/AppContextProps';
import { withAppContext } from '../../contexts/AppContext';
import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';

/**
 * hideEmptyContent (HOC)
 * WHAT: Wraps a section renderer (ContentDataSection) and renders NOTHING when the
 *       section's contentData is "effectively empty".
 * WHY:  In readonly/published view we don't want to show placeholder boxes for
 *       sections the author left blank. In edit mode we still want them visible
 *       so the author can fill them in — that's enforced at the call site.
 *
 * "Empty" means:
 *   - Tiptap JSON object: every top-level node has no nested content.
 *   - HTML string: no <img> AND stripping tags leaves only whitespace.
 */
const hideEmptyContent = (SectionComponent: any) => {
  return (props: ContentDataSectionProps) => {
    // True if the raw html string contains at least one <img ...> tag.
    // Images count as content even though they have no text.
    const containsImages = (str: any) => {
      return imgTagRegex.test(str);
    };

    // True if the string is falsy or only whitespace.
    const isBlank = (str: string) => {
      return !str || emptyOrSpaceOnlyRegex.test(str);
    };

    // Dispatches on the shape of `content`: Tiptap object vs raw HTML string.
    const isEmptyContent = (content: string | { content: object }) => {
      if (typeof content === 'object') {
        // Tiptap doc: { content: [ { content: [...] }, ... ] }
        // Empty only if every child node has no inner content.
        return Array.isArray(content?.content) && content?.content?.every((data) => !data?.content);
      } else {
        if (!content) {
          return true;
        }
        // Strip tags to test plain text, but still keep if there's an image.
        const textContent = content.replace(htmlTagsRegex, '');
        return !containsImages(content) && isBlank(textContent);
      }
    };

    if (!isEmptyContent(props.content.contentData)) {
      return (
        <div className="content-readonly-section">
          {/* colspan=12 forces full-width — Grid here is effectively a no-op wrapper.
              DEAD CODE / smell: a single-column Grid adds nothing; a plain <div> would do. */}
          <Grid gridDefinition={[{ colspan: 12 }]}>
            <SectionComponent {...props} />
          </Grid>
        </div>
      );
    } else {
      // Render an empty <div/> rather than null. Works but wastes a DOM node.
      return <div />;
    }
  };
};

/**
 * hideEmptyEpics (HOC)
 * WHAT: Same idea as hideEmptyContent but for the Epics section — hide it when
 *       there are zero epics.
 * WHY:  Epics have a completely different data shape (Epic[]), so the
 *       "is empty" check is just an array length check.
 *
 * NOTE: Unlike hideEmptyContent this HOC always renders PatternContentEpics
 *       in readonly mode (hard-coded `readonly`). That's fine because this
 *       wrapper is only used in the readonly path at the call site, but it
 *       makes the wrapper less reusable than its name suggests.
 */
const hideEmptyEpics = () => {
  return (props: PatternContentEpicsProps) => {
    // Destructure every prop so we can forward them explicitly. A `{...props}`
    // spread would have been equivalent and shorter — this explicit list is
    // largely boilerplate and gets out of sync when new props are added.
    const {
      epics,
      expanded,
      contentId,
      showSectionalComments,
      workflow,
      workflowId,
      coAuthors,
      workflowHistory,
      content,
      showTechwriterMetadata,
      sectionId,
    } = props;
    if (Array.isArray(epics) && epics.length > 0) {
      return (
        <div className="content-readonly-section">
          <PatternContentEpics
            epics={epics}
            readonly
            expanded={expanded}
            contentId={contentId}
            workflowId={workflowId}
            showSectionalComments={showSectionalComments}
            workflow={workflow}
            workflowHistory={workflowHistory}
            coAuthors={coAuthors}
            content={content}
            showTechwriterMetadata={showTechwriterMetadata}
            sectionId={sectionId}
          />
        </div>
      );
    } else {
      return <div />;
    }
  };
};

/**
 * hideEmptyTroubleshooting (HOC)
 * WHAT: Same empty-hiding pattern for the Troubleshooting section.
 * WHY:  Troubleshooting is an array of {issue, solution} — hide when empty.
 *
 * Also pulls `entities` from AppContext (global AWS service catalog) and
 * forwards it so the readonly view can resolve @-mentions inside the editor.
 */
const hideEmptyTroubleshooting = () => {
  return (props: PatternContentTroubleshootingProps) => {
    const {
      troubleshootings,
      expanded,
      contentId,
      showSectionalComments,
      workflow,
      workflowId,
      coAuthors,
      workflowHistory,
      content,
      showTechwriterMetadata,
      sectionId,
      appContext,
    } = props;

    // `entities` may be undefined if AppContext hasn't finished loading yet.
    const { entities } = appContext ?? {};

    if (Array.isArray(troubleshootings) && troubleshootings.length > 0) {
      return (
        <div className="content-readonly-section">
          <PatternContentTroubleshooting
            readonly
            troubleshootings={troubleshootings}
            expanded={expanded}
            contentId={contentId}
            workflowId={workflowId}
            showSectionalComments={showSectionalComments}
            workflow={workflow}
            workflowHistory={workflowHistory}
            coAuthors={coAuthors}
            content={content}
            showTechwriterMetadata={showTechwriterMetadata}
            sectionId={sectionId}
            entities={entities}
          />
        </div>
      );
    } else {
      return <div />;
    }
  };
};

// Pre-built wrapped components for the readonly path. Creating them once at
// module scope (rather than inside render) keeps React from remounting them
// on every render, which would reset editor state.
const NonEmptyPatternContentSection = hideEmptyContent(ContentDataSection);
const NonEmptyPatternContentEpics = hideEmptyEpics();
const NonEmptyPatternContentTroubleshooting = hideEmptyTroubleshooting();

/**
 * getContentLinkTarget
 * WHAT: Decide the target attribute for a link inside rendered content.
 * WHY:  Internal links (same origin, or fragment anchors like "#intro") should
 *       stay within the SPA; external links should open in a new tab so the
 *       user doesn't lose their place in the docs.
 */
const getContentLinkTarget = (link: string) => {
  if (link.startsWith(window.location.origin) || link.startsWith('#')) {
    return '_self';
  } else {
    return '_blank';
  }
};

/**
 * handleContentClick
 * WHAT: Click-delegation handler attached to every rendered section.
 * WHY:  The Tiptap editor renders anchors without target attributes. Rather
 *       than post-processing HTML on every render, we intercept clicks and
 *       set `target` on the fly based on whether the URL is internal/external.
 *
 * Only applies to links inside `.awscat-editor-view` or `.task-list-table`
 * — i.e., user-authored content, not UI chrome.
 */
// @ts-expect-error TS7006 - strict migration
const handleContentClick = (event) => {
  // Open all internal links in same tab and external links in new tab
  if (event && event.target) {
    const t = event.target;
    if (
      t.localName === 'a' &&
      (t.closest('.awscat-editor-view') || t.closest('.task-list-table'))
    ) {
      t.target = getContentLinkTarget(t.href);
    }
  }
};

/**
 * ContentSection
 * WHAT: A single section inside the sortable list.
 *
 * WHY it exists:
 *   Switches on the section's UIComponent (free-form editor / epics /
 *   troubleshooting). Its drag handle (in ContentSectionHeader) binds to the
 *   enclosing SortableList item, so this component knows nothing about dnd.
 *
 * BUG: the props are not typed — everything below is implicitly `any`. Give
 *      it an explicit interface when refactoring.
 */
const ContentSection = ({
  // @ts-expect-error TS7031 - strict migration
  contentId,
  // @ts-expect-error TS7031 - strict migration
  workflowId,
  // @ts-expect-error TS7031 - strict migration
  sectionId,
  // @ts-expect-error TS7031 - strict migration
  contentSection,
  // @ts-expect-error TS7031 - strict migration
  templateSchema,
  // @ts-expect-error TS7031 - strict migration
  readonly,
  // @ts-expect-error TS7031 - strict migration
  showTechwriterMetadata,
  // @ts-expect-error TS7031 - strict migration
  hideEmptySections,
  // @ts-expect-error TS7031 - strict migration
  onContentChanged,
  // @ts-expect-error TS7031 - strict migration
  onSectionUpdate,
  // @ts-expect-error TS7031 - strict migration
  onSectionDelete,
  // @ts-expect-error TS7031 - strict migration
  expanded,
  // @ts-expect-error TS7031 - strict migration
  showSectionalComments,
  // @ts-expect-error TS7031 - strict migration
  workflow,
  // @ts-expect-error TS7031 - strict migration
  workflowHistory,
  // @ts-expect-error TS7031 - strict migration
  coAuthors,
  // @ts-expect-error TS7031 - strict migration
  sectionSortable,
  // @ts-expect-error TS7031 - strict migration
  entities,
}) => {
  // Look up the section definition in the template (e.g. "summary" -> {label, UIComponent}).
  // May be undefined for user-added sections that don't exist in the template.
  const templateProp = templateSchema.properties[sectionId];
  // Prefer the author-specified title; fall back to the template label.
  const header = (contentSection && contentSection.title) || (templateProp && templateProp.label);
  // Different CSS for edit vs readonly (e.g., borders, toolbars).
  const contentSectionClassName = readonly ? 'content-readonly-section' : 'content-editing-section';

  /**
   * Picks the correct renderer for the section based on its UIComponent enum.
   * Returns either the renderer directly (edit mode) or a "hide if empty" wrapper (readonly).
   *
   * BUG: this switch has no `default` branch. If the template has an unknown
   *      UIComponent value, `sectionComponent` stays undefined and the
   *      function returns `undefined`, which React then renders as nothing
   *      — silent data loss. Add a default that throws or renders a fallback.
   */
  // @ts-expect-error TS7030 - strict migration
  const contentDataSection = (UIComponent: string, isEditable: boolean) => {
    let sectionComponent;

    switch (UIComponent) {
      case ContentUIComponent.ContentDataSection:
        // Free-form rich-text section (e.g., Summary, Assumptions, Architecture).
        sectionComponent = (
          <ContentDataSection
            contentId={contentId}
            workflowId={workflowId}
            workflow={workflow}
            workflowHistory={workflowHistory}
            sectionId={sectionId}
            header={header}
            content={contentSection}
            readonly={readonly}
            onContentChanged={onContentChanged}
            onSectionDeleted={onSectionDelete}
            onSectionUpdated={onSectionUpdate}
            showTechwriterMetadata={showTechwriterMetadata}
            sectionEditable={isEditable}
            sectionSortable={sectionSortable}
            expanded={expanded}
            showSectionalComments={showSectionalComments}
            coAuthors={coAuthors}
          />
        );
        // In readonly mode AND when hideEmptySections is enabled, route through
        // the HOC that renders nothing if the content is effectively blank.
        //
        // BUG: `sectionComponent.props` is passed to NonEmptyPatternContentSection.
        // This reads the props back off the React element — it works but it's
        // fragile: if ContentDataSection ever changes its prop shape or wraps
        // itself in another element, this silently breaks. Prefer passing the
        // props object directly.
        return hideEmptySections && readonly ? (
          <NonEmptyPatternContentSection {...sectionComponent.props} />
        ) : (
          <div className={contentSectionClassName} onClick={handleContentClick}>
            {sectionComponent}
          </div>
        );
      case ContentUIComponent.PatternContentEpics:
        // Epics = table of iteration-plan rows. Only valid for Patterns.
        sectionComponent = (
          <PatternContentEpics
            epics={contentSection && contentSection.contentData}
            readonly={readonly}
            onContentChanged={onContentChanged}
            expanded={expanded}
            contentId={contentId}
            workflowId={workflowId}
            showSectionalComments={showSectionalComments}
            workflow={workflow}
            workflowHistory={workflowHistory}
            coAuthors={coAuthors}
            content={contentSection}
            showTechwriterMetadata={showTechwriterMetadata}
            sectionId={sectionId}
          />
        );

        return hideEmptySections && readonly ? (
          <NonEmptyPatternContentEpics {...sectionComponent.props} />
        ) : (
          <div className={contentSectionClassName} onClick={handleContentClick}>
            {sectionComponent}
          </div>
        );
      case ContentUIComponent.PatternContentTroubleshooting:
        // Troubleshooting = table of {issue, solution} rows. Only valid for Patterns.
        //
        // Legacy data shape: older content stored a single Troubleshooting
        // object instead of an array. This normalizes both shapes to an array.
        const patternTroubleshootings = Array.isArray(contentSection?.contentData)
          ? contentSection?.contentData
          : [contentSection?.contentData];
        sectionComponent = (
          <PatternContentTroubleshooting
            troubleshootings={patternTroubleshootings}
            readonly={readonly}
            onContentChanged={onContentChanged}
            expanded={expanded}
            contentId={contentId}
            workflowId={workflowId}
            showSectionalComments={showSectionalComments}
            workflow={workflow}
            workflowHistory={workflowHistory}
            coAuthors={coAuthors}
            content={contentSection}
            showTechwriterMetadata={showTechwriterMetadata}
            sectionId={sectionId}
            entities={entities}
          />
        );

        return hideEmptySections && readonly ? (
          <NonEmptyPatternContentTroubleshooting {...sectionComponent.props} />
        ) : (
          <div className={contentSectionClassName} onClick={handleContentClick}>
            {sectionComponent}
          </div>
        );
    }
    // FALLTHROUGH BUG: if UIComponent doesn't match any case, returns undefined.
  };

  console.info('templateProp', templateProp);
  console.info('sectionId', sectionId);

  let dataSection;
  if (templateProp) {
    // Section is declared in the template: use the template's UIComponent.
    const { UIComponent } = templateProp;
    // Only Guide / Strategy sections can have their title edited. Pattern
    // sections are fixed (summary, assumptions, …) and cannot be renamed.
    const editable = guideOrStrategy(workflow?.draftContentMetadata?.contentType);
    dataSection = contentDataSection(UIComponent, editable);
  } else {
    // User-added custom section (Guide/Strategy only). Always a free-form editor
    // and always editable.
    dataSection = contentDataSection(ContentUIComponent.ContentDataSection, true);
  }
  // Render nothing (rather than undefined, which React 17 rejects) for an unknown UIComponent.
  return dataSection ?? null;
};

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfSection = (section: ContentNode) => section.id;

/** The ContentDataForm values the section list needs; passed explicitly rather than spread. */
type ContentSectionsProps = Pick<
  ContentDataFormProps,
  | 'contentId'
  | 'workflowId'
  | 'onContentChanged'
  | 'contentPage'
  | 'readonly'
  | 'hideEmptyContent'
  | 'templateSchema'
  | 'showTechwriterMetadata'
  | 'onSectionDeleted'
  | 'onSectionsUpdated'
  | 'expanded'
  | 'workflow'
  | 'workflowHistory'
  | 'coAuthors'
  | 'showSectionalComments'
  | 'sectionsSortable'
  | 'appContext'
>;

/**
 * ContentSections
 * WHAT: The drag-and-drop list holding every ContentSection plus its comment
 *       threads, backed by the shared dnd-kit SortableList.
 * WHY:  Each list item is the per-section wrapper div, so the section, its
 *       comments and the trailing <br/> move together while dragging.
 */
function ContentSections(props: ContentSectionsProps) {
  const {
    contentId,
    workflowId,
    onContentChanged,
    contentPage,
    readonly,
    // tslint:disable-next-line: no-shadowed-variable
    // `hideEmptyContent` here is the BOOLEAN prop, not the HOC defined above.
    // They share the name, which is the reason for the tslint-disable. Confusing —
    // rename the prop (e.g. `hideEmptySections`) during refactor.
    hideEmptyContent,
    templateSchema,
    showTechwriterMetadata,
    onSectionDeleted,
    onSectionsUpdated,
    expanded,
    workflow,
    workflowHistory,
    coAuthors,
    showSectionalComments,
    sectionsSortable,
    appContext,
  } = props;

  const { entities } = appContext ?? {};

  const contentSections = contentPage?.contentSections ?? [];

  // Dragging is only offered when the caller can persist the new order; the
  // read-only viewer passes no onSectionsUpdated.
  const isSortingDisabled = readonly || !sectionsSortable || onSectionsUpdated === undefined;

  /**
   * Apply a partial update (title, abstract, href, …) to a section and notify
   * the parent so it can persist.
   *
   * BUG: mutates `contentSection` in place (and thus `contentSections` / `contentPage`).
   * React props are supposed to be immutable; this works only because the parent
   * happens to keep the same reference. Use an immutable update instead.
   *
   * BUG: when the caller updates `title`, href is ALSO regenerated from the new
   * title. That's intentional for auto-slug, but it silently overwrites any
   * custom href the user set. Consider doing this only when href is empty.
   */
  const updateSection = (sectionId: string, attributes: ContentNode) => {
    const contentSection = contentSections.find((section) => section.id === sectionId);
    Object.keys(attributes).forEach((att) => {
      // @ts-expect-error TS7053 - strict migration
      contentSection[att] = attributes[att];
      // @ts-expect-error TS2345 - strict migration
      contentSection.href = getNodeHref(contentSection, workflow?.draftContentMetadata);
    });
    if (onSectionsUpdated) {
      onSectionsUpdated(contentSections);
    }
  };

  // Bubble the dropped order up for persistence; no-op drops never reach here.
  const reorderSections = (orderedIds: readonly string[]) => {
    onSectionsUpdated?.(reorderById(contentSections, orderedIds));
  };

  // One sortable wrapper per section holding the section plus its comment thread view.
  const renderSection = (
    contentSection: ContentNode,
    { setNodeRef, style }: SortableRenderArgs,
  ) => {
    const { id: sectionId, href } = contentSection;
    return (
      // The outer div's id is used as a deep-link anchor (e.g. #architecture).
      // Prefer href (human-readable slug) over the internal id when available.
      <div id={href || sectionId} ref={setNodeRef} style={style}>
        <ContentSection
          contentId={contentId}
          workflowId={workflowId}
          workflow={workflow}
          workflowHistory={workflowHistory}
          coAuthors={coAuthors}
          sectionId={sectionId}
          contentSection={contentSection}
          readonly={readonly}
          templateSchema={templateSchema}
          showTechwriterMetadata={showTechwriterMetadata}
          onContentChanged={onContentChanged}
          onSectionUpdate={updateSection}
          onSectionDelete={onSectionDeleted}
          hideEmptySections={hideEmptyContent}
          expanded={expanded}
          showSectionalComments={showSectionalComments}
          // Only show handles that can actually reorder; read-only views get none.
          sectionSortable={!isSortingDisabled}
          entities={entities}
        />
        {/* Comments pinned to this section. Shown only when showSectionalComments=true. */}
        <CommentThreadsView
          contentId={contentId}
          workflowId={workflowId}
          sectionId={sectionId}
          workflow={workflow}
          workflowHistory={workflowHistory}
          coAuthors={coAuthors}
          visible={showSectionalComments}
        />
        <br />
      </div>
    );
  };

  // id="page-content-sections" is used by scroll/anchor utilities elsewhere
  // and by e2e tests to locate the sections region.
  return (
    <SortableList
      items={contentSections}
      keyExtractor={keyOfSection}
      onReorder={reorderSections}
      isDisabled={isSortingDisabled}
      container={(children) => <div id="page-content-sections">{children}</div>}
      item={renderSection}
    />
  );
}

/**
 * Props accepted by ContentDataForm.
 *
 * `extends AppContextProps` gives us `appContext` via withAppContext() below.
 * `contentViewerContext` is injected by withContentViewerContext().
 *
 * The callback props are bubble-ups: the form itself doesn't persist anything,
 * it just tells the parent what changed so the parent can call the service.
 */
interface ContentDataFormProps extends AppContextProps {
  readonly contentId: string;
  readonly workflowId: number;
  readonly templateSchema: any; // JSON schema describing template sections (e.g., pattern template).
  readonly visible?: boolean; // If false, render nothing. Used by tabbed parents.
  readonly readonly?: boolean; // Editor vs reader mode.
  readonly contentPage?: ContentNode; // The page being rendered (root page or sub-page).
  // Fired whenever a single editor value changes. Granular so the parent can
  // debounce and validate per-field.
  readonly onContentChanged?: (
    sectionId: string,
    key: ContentSectionKey,
    newContent: string,
    validationError?: string,
  ) => void;
  // Fired after drag-and-drop reorder or after updateSection(). Replaces the full list.
  readonly onSectionsUpdated?: (contentSections: ContentNode[]) => void;
  readonly onSectionDeleted?: (sectionId: string) => void;
  // Present in the interface but never actually wired up through this component.
  // DEAD PROP: `onSectionEdited` is defined but never used in render().
  readonly onSectionEdited?: (
    sectionId: string,
    attributes: { title: any; shortTitle?: any },
  ) => void;
  readonly onPageDeleted?: (pageId: string) => void;
  readonly onPageTitleUpdated?: (
    pageId: string,
    title: TiptapOutputType,
    shortTitle?: TiptapOutputType,
  ) => void;
  readonly showTechwriterMetadata?: boolean; // Show abstract/href editors (tech-writer role only).
  readonly showSectionalComments?: boolean; // Show CommentThreadsView under each section.
  readonly showPage?: boolean; // Wrap the whole page in a Container/Box with its own header.
  readonly hideEmptyContent?: boolean; // In readonly mode, skip sections with no content.
  readonly expanded: boolean; // Expand all ExpandableSection wrappers by default.
  readonly workflow?: ContentWorkflow;
  readonly workflowHistory?: ContentHistory[];
  readonly coAuthors?: ContentAccessUser[];
  readonly contentViewerContext: ContentViewerContextData;
  readonly sectionsSortable?: boolean; // Whether drag handles are enabled on sections.
}

interface ContentDataFormState {
  // Whether the page-title edit modal is open.
  isEditingPageHeader: boolean;
  // Draft value of the page title being edited inside the modal.
  pageTitle: TiptapOutputType;
  // Draft value of the page short title being edited inside the modal.
  pageShortTitle: TiptapOutputType;
  // Present but never assigned anywhere — DEAD STATE. Remove when refactoring.
  pageTitleValidationError?: string;
}

/**
 * ContentDataForm (class component)
 * WHAT: The top-level component exported from this file. Composes everything above.
 * WHY class (not hook): historical — this existed before hooks. It holds only
 *      the page-title-edit modal state, so it could easily be rewritten as a
 *      function component with useState when you refactor.
 */
class ContentDataForm extends React.Component<ContentDataFormProps, ContentDataFormState> {
  // Defaults for optional props. `showValidationError` and `templateData` are
  // listed here but NOT declared on ContentDataFormProps — DEAD CODE (ignored
  // by React, pure noise).
  private static defaultProps = {
    hideEmptyContent: false,
    readonly: false,
    showPage: false,
    showTechwriterMetadata: false,
    showValidationError: false, // DEAD: not in props interface.
    templateData: {}, // DEAD: not in props interface.
    visible: true,
    workflowHistory: [],
  };

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      isEditingPageHeader: false,
      pageTitle: '',
      pageShortTitle: '',
    };
  }

  public render() {
    const {
      contentPage,
      contentId,
      workflowId,
      workflow,
      workflowHistory,
      templateSchema,
      visible,
      readonly,
      showPage,
      onPageDeleted,
      onContentChanged,
      showSectionalComments,
      coAuthors,
      hideEmptyContent,
      showTechwriterMetadata,
      onSectionDeleted,
      onSectionsUpdated,
      expanded,
      sectionsSortable,
      appContext,
    } = this.props;

    // Guard: nothing to render if parent hasn't loaded the page yet.
    if (!contentPage) {
      return null;
    }

    // Pull out the fields we need from the page node. `nodeType` distinguishes
    // Document (top-level page) from Section (sub-page).
    const {
      contentData,
      id: pageId,
      abstract: pageAbstract,
      href: pageHref,
      nodeType,
    } = contentPage;
    // `contentType` comes from the viewer context rather than the workflow so
    // that published (readonly) views, which don't have a draft workflow, still work.
    const contentType = this.props.contentViewerContext?.content?.contentMetadata?.contentType;

    // @ts-expect-error TS2345 - strict migration
    const isGuideOrStrategy = guideOrStrategy(contentType);
    // 'rootId' is the sentinel id of the top-level page.
    // Only Guide/Strategy sub-pages can have their title edited; the root page
    // title is the content's main title and is edited elsewhere.
    const isPageHeaderEditable = contentPage.id !== 'rootId' && isGuideOrStrategy;
    // Bundle the page-level tech-writer fields into the shape the editor expects.
    const techwriterMetadata = { abstract: pageAbstract, href: pageHref };

    // Second guard: bail out if not visible or the template isn't ready.
    // Could be combined with the contentPage check above.
    if (!visible || !templateSchema) {
      return null;
    }

    // Only render the "edit page title" modal in edit mode.
    const pageEditModal = !readonly ? this.renderPageEditModal() : null;
    // Pattern readonly view shows just the href (permalink) — Guides/Strategies
    // use a fuller editor rendered below. The ternary is equivalent to:
    //   show TechwriterMetadataEditor iff (readonly && !isGuideOrStrategy)
    const patternReadonlyPathslug =
      isGuideOrStrategy || !readonly ? null : (
        <TechwriterMetadataEditor
          readonly={readonly}
          // @ts-expect-error TS2322 - strict migration
          visible={this.props.showTechwriterMetadata}
          metadata={techwriterMetadata}
          nodeType={nodeType}
          contentType={contentType}
        />
      );

    // The list of child sections, drag-and-drop enabled.
    const contentSections = (
      <>
        {patternReadonlyPathslug}
        <ContentSections
          contentId={contentId}
          workflowId={workflowId}
          onContentChanged={onContentChanged}
          contentPage={contentPage}
          readonly={readonly}
          hideEmptyContent={hideEmptyContent}
          templateSchema={templateSchema}
          showTechwriterMetadata={showTechwriterMetadata}
          onSectionDeleted={onSectionDeleted}
          onSectionsUpdated={onSectionsUpdated}
          expanded={expanded}
          workflow={workflow}
          workflowHistory={workflowHistory}
          coAuthors={coAuthors}
          showSectionalComments={showSectionalComments}
          sectionsSortable={sectionsSortable}
          appContext={appContext}
        />
      </>
    );

    // The page-level inputs: tech-writer metadata for the page + the page body editor + page comments.
    const contentPageInput = (
      <div>
        <SpaceBetween size="xxxs" className="content-page">
          <TechwriterMetadataEditor
            // @ts-expect-error TS2322 - strict migration
            readonly={readonly}
            // @ts-expect-error TS2322 - strict migration
            visible={this.props.showTechwriterMetadata}
            metadata={techwriterMetadata}
            onInputUpdated={this.updateTechwriterMetadata}
            nodeType={nodeType}
            // For Patterns, tech-writer metadata is read-only at the page level
            // (it's generated) — only Guides/Strategies let writers edit it.
            disabled={!isGuideOrStrategy}
            metadataId={pageId}
            // Force re-mount when the page changes so the editor resets its internal state.
            key={pageId}
          />
          <div id={pageHref || pageId}>
            <EditorInput
              contentId={contentId}
              sectionId={pageId}
              data={contentData}
              readonly={readonly}
              onContentChanged={onContentChanged}
              content={contentPage}
              key={pageId}
            />
          </div>
        </SpaceBetween>
        <CommentThreadsView
          contentId={contentId}
          workflowId={workflowId}
          sectionId={pageId}
          workflow={workflow}
          visible={showSectionalComments}
          coAuthors={coAuthors}
          workflowHistory={workflowHistory}
        />
      </div>
    );

    // In readonly mode we use a plain Box (no container chrome). In edit mode
    // we wrap in a Container with a ContentSectionHeader so the author sees
    // edit/delete controls for the page itself.
    const pageContainer = readonly ? (
      <Box padding={{ horizontal: 's' }}>
        {contentPageInput}
        {contentSections}
        {pageEditModal}
        {/* "Add new section" is never useful in readonly mode. Guarded defensively. */}
        {isGuideOrStrategy && !readonly ? <AddNewSectionButton /> : null}
      </Box>
    ) : (
      <Container
        header={
          <Header variant="h3">
            <ContentSectionHeader
              sectionId={contentPage.id}
              title={contentPage.title}
              isEditable={isPageHeaderEditable}
              isSortable={false}
              onSectionDelete={onPageDeleted}
              onSectionEditClick={this.enablePageHeaderEditing}
              readonly={!!readonly}
            />
          </Header>
        }
      >
        {contentPageInput}
        {contentSections}
        {pageEditModal}
        {isGuideOrStrategy ? <AddNewSectionButton /> : null}
      </Container>
    );

    // `showPage=false` is the "sections only" mode used by the pattern viewer
    // where the page header is rendered by an outer component.
    const content = showPage ? pageContainer : contentSections;
    // Anchor id used by print stylesheet and e2e tests. Only applied in readonly mode.
    const contentDataFormId = readonly ? 'main-content' : '';

    return <div id={contentDataFormId}>{content}</div>;
  }

  /**
   * Open the "Edit Page title" modal and seed the draft title with the current value.
   */
  private enablePageHeaderEditing = () => {
    const { contentPage } = this.props;
    this.setState({
      isEditingPageHeader: true,
      // @ts-expect-error TS18048 - strict migration
      pageTitle: contentPage.title,
      // @ts-expect-error TS18048 - strict migration
      pageShortTitle: contentPage.shortTitle || '',
    });
  };

  /**
   * Close the modal and reset the draft back to the canonical title.
   *
   * DEAD CODE: `pageTitle` is reset here, but the modal is closed whenever
   * `isEditingPageHeader` flips to false, so re-syncing the draft value is
   * only needed on cancel. Harmless but confusing — consider removing the
   * reset and doing it lazily in enablePageHeaderEditing instead.
   */
  private disablePageHeaderEditing = () => {
    const { contentPage } = this.props;
    this.setState({
      isEditingPageHeader: false,
      // @ts-expect-error TS18048 - strict migration
      pageTitle: contentPage.title,
      // @ts-expect-error TS18048 - strict migration
      pageShortTitle: contentPage.shortTitle || '',
    });
  };

  /**
   * Controlled-input callback for the page-title Tiptap editor in the modal.
   */
  private updatePageTitle = (content: TiptapOutputType) => {
    this.setState({ pageTitle: content });
  };

  /**
   * Controlled-input callback for the page short title input in the modal.
   */
  private updatePageShortTitle = (value: TiptapOutputType) => {
    this.setState({ pageShortTitle: value });
  };

  /**
   * Save handler for the modal. Bubbles the new title up to the parent and closes the modal.
   *
   * BUG: no validation — an empty pageTitle will still fire onPageTitleUpdated.
   * The modal's FormField shows errorText when empty but the Save button isn't
   * disabled, so the invalid value can be submitted.
   */
  private editPageTitle = () => {
    const { pageTitle, pageShortTitle } = this.state;
    const { contentPage, onPageTitleUpdated } = this.props;
    if (onPageTitleUpdated) {
      // @ts-expect-error TS18048 - strict migration
      onPageTitleUpdated(contentPage.id, pageTitle, pageShortTitle || undefined);
    }
    this.setState({
      isEditingPageHeader: false,
    });
  };

  /**
   * Render the modal used to edit the page title.
   *
   * Uses EditorWrapper (plain Tiptap without comment/mention extensions)
   * because a title doesn't need the full editor feature set.
   *
   * BUG: FormField.errorText is assigned a boolean (`!pageTitle`) instead of
   * a string message. CloudScape will coerce it, but the visible error text
   * will literally be "true" / nothing instead of a helpful message.
   *
   * BUG: dismissing by clicking outside or pressing ESC is ignored — only the
   * close button wires up to `disablePageHeaderEditing`. The modal can then
   * get stuck open if the user clicks the overlay. (Contrast with
   * ContentDataSection which handles DismissModalReason explicitly.)
   */
  private renderPageEditModal = () => {
    const { isEditingPageHeader, pageTitle, pageShortTitle } = this.state;
    const { contentPage } = this.props;
    const formActions = (
      <div>
        <Button onClick={this.disablePageHeaderEditing} variant="link">
          {constants.CANCEL_BUTTON_TEXT}
        </Button>
        <Button onClick={this.editPageTitle} variant="primary">
          {constants.SAVE_BUTTON_TEXT}
        </Button>
      </div>
    );
    // Only instantiate the editor when the modal is open. Prevents it from
    // ghost-rendering (and grabbing focus) when the modal is closed.
    const pageTitleEditor = isEditingPageHeader ? (
      <EditorWrapper
        model={pageTitle}
        readOnly={false}
        onModelChange={this.updatePageTitle}
        menuType={TiptapMenuType.Hide}
        // @ts-expect-error TS18048 - strict migration
        id={'page-title-' + contentPage.id}
      />
    ) : null;

    const pageShortTitleEditor = isEditingPageHeader ? (
      <EditorWrapper
        model={pageShortTitle}
        readOnly={false}
        onModelChange={this.updatePageShortTitle}
        menuType={TiptapMenuType.Hide}
        removeFormatting
        singleLine
        id={'page-short-title-' + contentPage?.id}
      />
    ) : null;

    return (
      <Modal
        onDismiss={this.disablePageHeaderEditing}
        header={'Edit Page'}
        visible={isEditingPageHeader}
        footer={formActions}
        closeAriaLabel="Close modal"
      >
        <SpaceBetween size="l">
          <FormField
            errorText={!pageTitle}
            label={'Page title'}
            stretch
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            {pageTitleEditor}
          </FormField>
          <FormField
            label={
              <>
                {constants.SHORT_TITLE_LABEL} - <i>optional</i>
              </>
            }
            description={constants.SHORT_TITLE_NODE_DESCRIPTION}
            stretch
          >
            {pageShortTitleEditor}
          </FormField>
        </SpaceBetween>
      </Modal>
    );
  };

  /**
   * Handler used by the page-level TechwriterMetadataEditor when a writer
   * changes the abstract or href.
   *
   * WHY the tree walk: the editor calls back with a single field change on
   * `currentPage`, but `currentPage` may be a DEEP sub-node of the content
   * tree. We update the in-memory copy AND locate the same node in
   * `content.contentData` so that saving the whole Content blob persists the change.
   *
   * BUG: `validationError` parameter is accepted (matching the shared callback
   * signature) but IGNORED here. Invalid values silently propagate into
   * `updateContent`.
   *
   * BUG: mutates `currentPage` and the matched node in place. Same
   * immutability concern as updateSection() above.
   */
  private updateTechwriterMetadata = (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string, // DEAD PARAM: not used.
  ) => {
    const { currentPage, content, updateContent } = this.props.contentViewerContext;
    // @ts-expect-error TS18048 - strict migration
    currentPage[sectionKey] = value;
    // Find the same node inside the content tree (by id) and mirror the change
    // onto it. Needed because `currentPage` may be a shallow snapshot.
    // @ts-expect-error TS18048 - strict migration
    const currentPageNode = findNodeById(currentPage.id, [content.contentData]);
    if (currentPageNode) {
      Object.assign(currentPageNode, { ...currentPage });
    }
    // @ts-expect-error TS2722 - strict migration
    updateContent(content);
  };
}

// Compose the two context HOCs. Order matters only in that the INNER HOC runs
// first: withAppContext injects `appContext`, then withContentViewerContext
// injects `contentViewerContext`. Both are then available on this.props.
export default withContentViewerContext(withAppContext(ContentDataForm));
